export type SharedConfig = {
  pocketbaseUrl: string;
  liveServerUrl: string;
  /** Base URL for the Next.js API routes. Empty string on web (relative
   *  fetches); absolute production URL in the bundled mobile app. */
  webApiUrl: string;
  reportError: (error: unknown, context?: Record<string, unknown>) => void;
  capture?: (event: string, properties?: Record<string, unknown>) => void;
  /** Native-only: set screen brightness (0-1); null restores the previous
   *  level. No-op on web, where browsers cannot control brightness. */
  setScreenBrightness?: (level: number | null) => Promise<void> | void;
  /** Native-only: the installed app's version, read from the native package at
   *  runtime so it always matches what the store actually installed. Absent on
   *  web, where there is no app version to speak of. */
  getAppVersion?: () => Promise<AppVersion | null>;
};

/** `version` is the user-facing versionName ("1.0"); `build` is the
 *  versionCode ("13"), which is what identifies the upload on Google Play. */
export type AppVersion = {
  version: string;
  build: string;
};

let config: SharedConfig | null = null;

export function configureShared(next: SharedConfig): void {
  config = next;
}

export function getSharedConfig(): SharedConfig {
  if (!config) {
    throw new Error(
      'configureShared() must be called before using @racefriends/shared',
    );
  }
  return config;
}

/** Analytics capture that is safe to call before configureShared (no-op). */
export function captureEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  config?.capture?.(event, properties);
}

/** Screen brightness control that is safe to call on any host (no-op when
 *  the host does not provide it, e.g. web). */
export function setScreenBrightness(level: number | null): void {
  void config?.setScreenBrightness?.(level);
}

/** Installed app version, or null on hosts that don't have one (web). Safe to
 *  call before configureShared. */
export async function getAppVersion(): Promise<AppVersion | null> {
  try {
    return (await config?.getAppVersion?.()) ?? null;
  } catch {
    return null;
  }
}
